import { useState } from 'react';
import SpaceBetween from '@cloudscape-design/components/space-between';
import Button from '@cloudscape-design/components/button';
import ButtonDropdown from '@cloudscape-design/components/button-dropdown';
import Header from '@cloudscape-design/components/header';
import TextFilter from '@cloudscape-design/components/text-filter';
import Table from '@cloudscape-design/components/table';
import Pagination from '@cloudscape-design/components/pagination';
import Badge from '@cloudscape-design/components/badge';
import Box from '@cloudscape-design/components/box';
import Link from '@cloudscape-design/components/link';
import Modal from '@cloudscape-design/components/modal';
import ProgressBar from '@cloudscape-design/components/progress-bar';
import Select, { SelectProps } from '@cloudscape-design/components/select';
import Toggle from '@cloudscape-design/components/toggle';
import { useQueryClient } from '@tanstack/react-query';
import { CohortParticipant, cohortsApi } from '../../api/cohorts';
import {
  useCohortParticipants,
  useUnenrollUser,
  useEnrollUser,
  useBulkEnroll,
  useDeleteEnrollment,
  useBulkDeleteEnrollments,
} from '../../hooks/queries';
import { useMentors } from '../../hooks/queries/useMentors';
import { queryKeys } from '../../lib/queryClient';
import { LoadingSpinner } from '../../components/LoadingSpinner';
import { ErrorAlert } from '../../components/ErrorAlert';
import { UserLink } from '../../components/UserLink';
import { AddParticipantModal } from '../../components/AddParticipantModal';
import { AddByTeamModal } from '../../components/AddByTeamModal';
import { useNotifications } from '../../components/NotificationFlashbar';
import '../../styles/table-hover.css';
import { badgePhotoUrl, phoneToolUrl } from '../../lib/externalUrls';

import { useIsProgramAdminOrOwner } from '../../hooks/usePermissions';

interface ParticipantsTabProps {
  cohortId: string;
  programId: string;
  mentorsEnabled?: boolean;
}

function getStatusBadgeColor(status: string): 'green' | 'grey' | 'blue' {
  switch (status) {
    case 'enrolled':  return 'green';
    case 'withdrawn': return 'grey';
    case 'graduated': return 'blue';
    default:          return 'grey';
  }
}

function formatStatus(status: string): string {
  return status.charAt(0).toUpperCase() + status.slice(1);
}

/**
 * Inline mentor assignment dropdown for admin view.
 */
function MentorAssignmentCell({
  item,
  cohortId,
  mentorOptions,
}: {
  item: CohortParticipant;
  cohortId: string;
  mentorOptions: SelectProps.Option[];
}) {
  const queryClient = useQueryClient();
  const [isAssigning, setIsAssigning] = useState(false);

  const currentValue = item.mentorId
    ? mentorOptions.find((opt) => opt.value === item.mentorId) ?? null
    : null;

  const handleChange = async (detail: SelectProps.ChangeDetail) => {
    const selectedMentorId = detail.selectedOption.value;
    if (!selectedMentorId || selectedMentorId === item.mentorId) return;
    setIsAssigning(true);
    try {
      await cohortsApi.assignMentor(cohortId, selectedMentorId, item.userId);
      queryClient.invalidateQueries({ queryKey: queryKeys.cohorts.participants(cohortId) });
      queryClient.invalidateQueries({ queryKey: queryKeys.mentors.list(cohortId) });
    } catch {
      // Error handled silently
    } finally {
      setIsAssigning(false);
    }
  };

  return (
    <Select
      selectedOption={currentValue}
      onChange={({ detail }) => handleChange(detail)}
      options={mentorOptions}
      placeholder="Unassigned"
      disabled={isAssigning}
      loadingText="Assigning..."
      statusType={isAssigning ? 'loading' : 'finished'}
      expandToViewport
    />
  );
}

export function ParticipantsTab({ cohortId, programId, mentorsEnabled = true }: ParticipantsTabProps) {
  const [participantsFilterText, setParticipantsFilterText] = useState('');
  const [currentPageIndex, setCurrentPageIndex] = useState(1);
  const [sortingColumn, setSortingColumn] = useState<{ sortingField: string }>({ sortingField: 'userName' });
  const [sortingDescending, setSortingDescending] = useState(false);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showAddByTeamModal, setShowAddByTeamModal] = useState(false);
  const [showWithdrawn, setShowWithdrawn] = useState(false);
  const [selectedItems, setSelectedItems] = useState<CohortParticipant[]>([]);
  const [isBulkActioning, setIsBulkActioning] = useState(false);
  // Pending delete: single userId string, or 'bulk' sentinel for bulk delete
  const [pendingDelete, setPendingDelete] = useState<string | 'bulk' | null>(null);
  const [pendingWithdraw, setPendingWithdraw] = useState<string | 'bulk' | null>(null);

  const queryClient = useQueryClient();
  const { showSuccess } = useNotifications();
  const isAdmin = useIsProgramAdminOrOwner(programId);
  const { mutateAsync: unenrollUser } = useUnenrollUser();
  const { mutateAsync: enrollUser } = useEnrollUser();
  const { mutateAsync: bulkEnroll } = useBulkEnroll();
  const { mutateAsync: deleteEnrollment } = useDeleteEnrollment();
  const { mutateAsync: bulkDeleteEnrollments } = useBulkDeleteEnrollments();
  const pageSize = 10;

  const { data: mentors } = useMentors(mentorsEnabled && isAdmin ? cohortId : undefined);

  const {
    data: participantsResponse,
    isLoading,
    error,
  } = useCohortParticipants(cohortId, isAdmin ? undefined : 'enrolled');

  if (isLoading) return <LoadingSpinner message="Loading participants..." />;
  if (error) return <ErrorAlert error={error} />;

  const allParticipants = participantsResponse?.participants || [];

  // Count current mentor assignments from participant data for live counts in the dropdown
  const mentorAssignmentCounts: Record<string, number> = {};
  for (const p of allParticipants) {
    if (p.mentorId) {
      mentorAssignmentCounts[p.mentorId] = (mentorAssignmentCounts[p.mentorId] ?? 0) + 1;
    }
  }

  const mentorOptions: SelectProps.Option[] = (mentors || []).map((mentor) => ({
    label: `${mentor.mentorName || mentor.mentorId} (${mentorAssignmentCounts[mentor.mentorId] ?? 0})`,
    value: mentor.mentorId,
  }));

  // Admins can toggle withdrawn visibility; others never see withdrawn
  const visibleParticipants = isAdmin && !showWithdrawn
    ? allParticipants.filter(p => p.enrollmentStatus !== 'withdrawn')
    : allParticipants;

  const filteredParticipants = visibleParticipants.filter(p =>
    (p.userName || '').toLowerCase().includes(participantsFilterText.toLowerCase()) ||
    (p.userEmail || '').toLowerCase().includes(participantsFilterText.toLowerCase())
  );

  const sortedParticipants = [...filteredParticipants].sort((a, b) => {
    const field = sortingColumn.sortingField;
    let aValue: any;
    let bValue: any;
    if (field === 'mentor') {
      aValue = a.mentorName || '';
      bValue = b.mentorName || '';
    } else if (field === 'progress') {
      aValue = 0;
      bValue = 0;
    } else {
      aValue = a[field as keyof CohortParticipant] || '';
      bValue = b[field as keyof CohortParticipant] || '';
    }
    if (aValue < bValue) return sortingDescending ? 1 : -1;
    if (aValue > bValue) return sortingDescending ? -1 : 1;
    return 0;
  });

  const pagesCount = Math.ceil(sortedParticipants.length / pageSize);
  const paginatedParticipants = sortedParticipants.slice(
    (currentPageIndex - 1) * pageSize,
    currentPageIndex * pageSize
  );

  // ── Single-row handlers ──────────────────────────────────────────────────

  const handleWithdraw = async (userId: string) => {
    await unenrollUser({ cohortId, userId });
    setSelectedItems(prev => prev.filter(p => p.userId !== userId));
    showSuccess('Participant withdrawn', 'The participant has been withdrawn from the cohort.');
    setPendingWithdraw(null);
  };

  const handleReenroll = async (participant: CohortParticipant) => {
    await enrollUser({ cohortId, userId: participant.userId, programId });
    setSelectedItems(prev => prev.filter(p => p.userId !== participant.userId));
    showSuccess('Participant re-enrolled', 'The participant has been re-enrolled in the cohort.');
  };

  const handleDelete = async (userId: string) => {
    setIsBulkActioning(true);
    try {
      await deleteEnrollment({ cohortId, userId });
      setSelectedItems(prev => prev.filter(p => p.userId !== userId));
      showSuccess('Record deleted', 'The enrollment record has been permanently deleted.');
      setPendingDelete(null);
    } finally {
      setIsBulkActioning(false);
    }
  };

  // ── Bulk handlers ────────────────────────────────────────────────────────

  const handleBulkWithdraw = async () => {
    const targets = selectedItems.filter(p => p.enrollmentStatus === 'enrolled');
    if (!targets.length) return;
    setIsBulkActioning(true);
    try {
      await Promise.all(targets.map(p => unenrollUser({ cohortId, userId: p.userId })));
      showSuccess(
        'Participants withdrawn',
        `${targets.length} participant${targets.length !== 1 ? 's' : ''} withdrawn.`
      );
      setSelectedItems([]);
    } finally {
      setIsBulkActioning(false);
      setPendingWithdraw(null);
    }
  };

  const handleBulkReenroll = async () => {
    const targets = selectedItems.filter(p => p.enrollmentStatus === 'withdrawn');
    if (!targets.length) return;
    setIsBulkActioning(true);
    try {
      const result = await bulkEnroll({
        cohortId,
        users: targets.map(p => ({ userId: p.userId, userName: p.userName, userEmail: p.userEmail })),
      });
      const successCount = result?.results?.success?.length ?? targets.length;
      const failedCount  = result?.results?.failed?.length ?? 0;
      if (failedCount > 0) {
        showSuccess(
          'Partially re-enrolled',
          `${successCount} re-enrolled, ${failedCount} failed.`
        );
      } else {
        showSuccess(
          'Participants re-enrolled',
          `${successCount} participant${successCount !== 1 ? 's' : ''} re-enrolled.`
        );
      }
      setSelectedItems([]);
    } finally {
      setIsBulkActioning(false);
    }
  };

  const handleBulkDelete = async () => {
    const targets = selectedItems.filter(p => p.enrollmentStatus === 'withdrawn');
    if (!targets.length) return;
    setIsBulkActioning(true);
    try {
      await bulkDeleteEnrollments({ cohortId, userIds: targets.map(p => p.userId) });
      showSuccess(
        'Records deleted',
        `${targets.length} enrollment record${targets.length !== 1 ? 's' : ''} permanently deleted.`
      );
      setSelectedItems([]);
    } finally {
      setIsBulkActioning(false);
      setPendingDelete(null);
    }
  };

  // ── Derived selection state ──────────────────────────────────────────────

  const selectedEnrolled  = selectedItems.filter(p => p.enrollmentStatus === 'enrolled');
  const selectedWithdrawn = selectedItems.filter(p => p.enrollmentStatus === 'withdrawn');
  const hasSelection      = selectedItems.length > 0;

  // Build Actions dropdown items — always present but items disabled when irrelevant
  const actionsItems = [
    {
      id: 'withdraw',
      text: `Withdraw selected (${selectedEnrolled.length})`,
      disabled: selectedEnrolled.length === 0,
      disabledReason: 'Select enrolled participants to withdraw',
    },
    {
      id: 'reenroll',
      text: `Re-enroll selected (${selectedWithdrawn.length})`,
      disabled: selectedWithdrawn.length === 0,
      disabledReason: 'Select withdrawn participants to re-enroll',
    },
    {
      id: 'delete',
      text: `Delete selected (${selectedWithdrawn.length})`,
      disabled: selectedWithdrawn.length === 0,
      disabledReason: 'Select withdrawn participants to delete',
    },
  ];

  const actionsOnItemClick = ({ detail }: { detail: { id: string } }) => {
    if (detail.id === 'withdraw') setPendingWithdraw('bulk');
    if (detail.id === 'reenroll') handleBulkReenroll();
    if (detail.id === 'delete')   setPendingDelete('bulk');
  };

  const withdrawnCount = allParticipants.filter(p => p.enrollmentStatus === 'withdrawn').length;

  return (
    <SpaceBetween size="l">
      {/* Toolbar */}
      {isAdmin && (
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '8px' }}>
          <SpaceBetween direction="horizontal" size="xs">
            <Button iconName="add-plus" onClick={() => setShowAddModal(true)}>Add participant</Button>
            <Button iconName="add-plus" onClick={() => setShowAddByTeamModal(true)}>Add by manager</Button>
            <ButtonDropdown
              items={actionsItems}
              disabled={!hasSelection}
              loading={isBulkActioning}
              onItemClick={actionsOnItemClick}
            >
              Actions
            </ButtonDropdown>
          </SpaceBetween>
          {withdrawnCount > 0 && (
            <Toggle
              checked={showWithdrawn}
              onChange={({ detail }) => {
                setShowWithdrawn(detail.checked);
                setSelectedItems([]);
              }}
            >
              Show withdrawn ({withdrawnCount})
            </Toggle>
          )}
        </div>
      )}

      <AddParticipantModal
        visible={showAddModal}
        cohortId={cohortId}
        programId={programId}
        onDismiss={() => setShowAddModal(false)}
        onSuccess={() => {
          queryClient.invalidateQueries({ queryKey: queryKeys.cohorts.participants(cohortId) });
          showSuccess('Participant added', 'The participant has been enrolled in the cohort.');
        }}
      />
      <AddByTeamModal
        visible={showAddByTeamModal}
        cohortId={cohortId}
        programId={programId}
        onDismiss={() => setShowAddByTeamModal(false)}
        onSuccess={() => {
          queryClient.invalidateQueries({ queryKey: queryKeys.cohorts.participants(cohortId) });
        }}
      />

      <div className="custom-hover-table-container">
        <Table
          trackBy="userId"
          sortingDisabled={false}
          sortingColumn={sortingColumn}
          sortingDescending={sortingDescending}
          onSortingChange={({ detail }) => {
            setSortingColumn(detail.sortingColumn as { sortingField: string });
            setSortingDescending(detail.isDescending || false);
          }}
          selectionType={isAdmin ? 'multi' : undefined}
          selectedItems={selectedItems}
          onSelectionChange={({ detail }) => setSelectedItems(detail.selectedItems)}
          header={
            <Header
              counter={
                hasSelection
                  ? `(${selectedItems.length}/${filteredParticipants.length})`
                  : `(${filteredParticipants.length})`
              }
            >
              Participants
            </Header>
          }
          filter={
            <TextFilter
              filteringPlaceholder="Find participants"
              filteringText={participantsFilterText}
              onChange={({ detail }) => setParticipantsFilterText(detail.filteringText)}
              countText={`${filteredParticipants.length} ${filteredParticipants.length === 1 ? 'match' : 'matches'}`}
            />
          }
          pagination={
            <Pagination
              currentPageIndex={currentPageIndex}
              pagesCount={pagesCount}
              onChange={({ detail }) => setCurrentPageIndex(detail.currentPageIndex)}
            />
          }
          columnDefinitions={[
            {
              id: 'userName',
              header: 'Name',
              cell: (item) => (
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <img
                    src={badgePhotoUrl(item.userId)}
                    alt={item.userName}
                    style={{ width: 32, height: 32, borderRadius: 5, objectFit: 'cover', flexShrink: 0 }}
                  />
                  <div>
                    <div>{item.userName} {item.isMentor && <Badge color="green">Mentor</Badge>}</div>
                    <div style={{ fontSize: '12px' }}>
                      <Link href={phoneToolUrl(item.userId)} external fontSize="body-s">
                        @{item.userId}
                      </Link>
                    </div>
                  </div>
                </div>
              ),
              sortingField: 'userName',
              isRowHeader: true,
            },
            {
              id: 'userEmail',
              header: 'Email',
              cell: (item) => <Link href={`mailto:${item.userEmail}`}>{item.userEmail}</Link>,
              sortingField: 'userEmail',
            },
            ...(mentorsEnabled ? [{
              id: 'mentor',
              header: 'Mentor',
              cell: (item: CohortParticipant) => {
                if (isAdmin) {
                  return (
                    <MentorAssignmentCell
                      item={item}
                      cohortId={cohortId}
                      mentorOptions={mentorOptions}
                    />
                  );
                }
                return item.mentorName && item.mentorId
                  ? <UserLink userName={item.mentorName} userId={item.mentorId} />
                  : '-';
              },
              sortingField: 'mentor',
            }] : []),
            {
              id: 'enrolledAt',
              header: 'Enrolled',
              cell: (item) => new Date(item.enrolledAt).toLocaleDateString(),
              sortingField: 'enrolledAt',
            },
            {
              id: 'progress',
              header: 'Progress',
              cell: (item) => (
                <ProgressBar value={item.completionPercentage ?? 0} variant="standalone" additionalInfo="" description="" />
              ),
              sortingField: 'completionPercentage',
            },
            {
              id: 'enrollmentStatus',
              header: 'Status',
              cell: (item) => (
                <Badge color={getStatusBadgeColor(item.enrollmentStatus)}>
                  {formatStatus(item.enrollmentStatus)}
                </Badge>
              ),
              sortingField: 'enrollmentStatus',
            },
            // Per-row actions — admin only, not read-only, not graduated
            ...(isAdmin ? [{
              id: 'actions',
              header: '',
              cell: (item: CohortParticipant) => {
                if (item.enrollmentStatus === 'graduated') return null;

                const rowItems = item.enrollmentStatus === 'withdrawn'
                  ? [
                      { id: 'reenroll', text: 'Re-enroll' },
                      { id: 'delete',   text: 'Delete record' },
                    ]
                  : [
                      { id: 'withdraw', text: 'Withdraw' },
                    ];

                return (
                  <ButtonDropdown
                    variant="icon"
                    items={rowItems}
                    ariaLabel="Participant actions"
                    expandToViewport
                    onItemClick={({ detail }) => {
                      if (detail.id === 'withdraw') setPendingWithdraw(item.userId);
                      if (detail.id === 'reenroll') handleReenroll(item);
                      if (detail.id === 'delete')   setPendingDelete(item.userId);
                    }}
                  />
                );
              },
            }] : []),
          ]}
          empty={
            <Box color="inherit" textAlign="center">
              <Box color="inherit" textAlign="center" variant="strong">No participants</Box>
              <Box color="inherit" padding={{ bottom: 's' }} variant="p">No participants to display.</Box>
            </Box>
          }
          items={paginatedParticipants}
          loadingText="Loading participants"
          variant="borderless"
        />
      </div>

      {/* Withdraw confirmation modal — covers both single and bulk withdraw */}
      {(() => {
        const isBulk = pendingWithdraw === 'bulk';
        const bulkTargets = selectedItems.filter(p => p.enrollmentStatus === 'enrolled');
        const singleName = !isBulk && pendingWithdraw
          ? (allParticipants.find(p => p.userId === pendingWithdraw)?.userName ?? pendingWithdraw)
          : '';

        const formatBulkNames = (targets: CohortParticipant[]) => {
          const names = targets.map(p => p.userName || p.userId);
          if (names.length <= 5) {
            if (names.length === 1) return <strong>{names[0]}</strong>;
            return <><strong>{names.slice(0, -1).join(', ')}</strong> and <strong>{names[names.length - 1]}</strong></>;
          }
          return <><strong>{names.slice(0, 5).join(', ')}</strong> and <strong>{names.length - 5} others</strong></>;
        };

        return (
          <Modal
            visible={!!pendingWithdraw}
            onDismiss={() => setPendingWithdraw(null)}
            header={isBulk ? 'Withdraw participants' : 'Withdraw participant'}
            footer={
              <Box float="right">
                <SpaceBetween direction="horizontal" size="xs">
                  <Button variant="link" onClick={() => setPendingWithdraw(null)}>Cancel</Button>
                  <Button
                    variant="primary"
                    loading={isBulkActioning}
                    onClick={() => {
                      if (isBulk) handleBulkWithdraw();
                      else if (pendingWithdraw) handleWithdraw(pendingWithdraw);
                    }}
                  >
                    Withdraw
                  </Button>
                </SpaceBetween>
              </Box>
            }
          >
            <Box>
              {isBulk
                ? <>Are you sure you want to withdraw {formatBulkNames(bulkTargets)} from the cohort? They can be re-enrolled later.</>
                : <>Are you sure you want to withdraw <strong>{singleName}</strong> from the cohort? They can be re-enrolled later.</>
              }
            </Box>
          </Modal>
        );
      })()}

      {/* Delete confirmation modal — covers both single and bulk delete */}
      {(() => {
        const isBulk = pendingDelete === 'bulk';
        const bulkTargets = selectedItems.filter(p => p.enrollmentStatus === 'withdrawn');
        const singleName = !isBulk && pendingDelete
          ? (allParticipants.find(p => p.userId === pendingDelete)?.userName ?? pendingDelete)
          : '';

        // Format bulk names: list up to 5, then "and N others"
        const formatBulkNames = (targets: CohortParticipant[]) => {
          const names = targets.map(p => p.userName || p.userId);
          if (names.length <= 5) {
            if (names.length === 1) return <strong>{names[0]}</strong>;
            return <><strong>{names.slice(0, -1).join(', ')}</strong> and <strong>{names[names.length - 1]}</strong></>;
          }
          return <><strong>{names.slice(0, 5).join(', ')}</strong> and <strong>{names.length - 5} others</strong></>;
        };

        return (
          <Modal
            visible={!!pendingDelete}
            onDismiss={() => setPendingDelete(null)}
            header={isBulk ? 'Delete enrollment records' : 'Delete enrollment record'}
            footer={
              <Box float="right">
                <SpaceBetween direction="horizontal" size="xs">
                  <Button variant="link" onClick={() => setPendingDelete(null)}>Cancel</Button>
                  <Button
                    variant="primary"
                    loading={isBulkActioning}
                    onClick={() => {
                      if (isBulk) handleBulkDelete();
                      else if (pendingDelete) handleDelete(pendingDelete);
                    }}
                  >
                    Delete
                  </Button>
                </SpaceBetween>
              </Box>
            }
          >
            <Box>
              {isBulk
                ? <>Are you sure you want to permanently delete the enrollment records for {formatBulkNames(bulkTargets)}? This action cannot be undone.</>
                : <>Are you sure you want to permanently delete the enrollment record for <strong>{singleName}</strong>? This action cannot be undone.</>
              }
            </Box>
          </Modal>
        );
      })()}
    </SpaceBetween>
  );
}
